/* eslint-disable react/prop-types */
import React, { useState, useEffect } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  getExpandedRowModel,
} from '@tanstack/react-table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IndeterminateCheckbox from 'components/common/advance-table/IndeterminateCheckbox';

const selectionColumn = (selectionColumnWidth, selectionHeaderClassname) => {
  return {
    id: 'selection',
    accessorKey: '',
    header: ({ table }) => (
      <IndeterminateCheckbox
        className="form-check mb-0"
        {...{
          checked: table.getIsAllRowsSelected(),
          indeterminate: table.getIsSomeRowsSelected(),
          onChange: table.getToggleAllRowsSelectedHandler(),
        }}
      />
    ),
    cell: ({ row }) => (
      <IndeterminateCheckbox
        className="form-check mb-0"
        {...{
          checked: row.getIsSelected(),
          disabled: !row.getCanSelect(),
          indeterminate: row.getIsSomeSelected(),
          onChange: row.getToggleSelectedHandler(),
        }}
      />
    ),
    meta: {
      headerProps: {
        className: selectionHeaderClassname,
        style: {
          width: selectionColumnWidth,
        },
      },
      cellProps: {
        style: {
          width: selectionColumnWidth,
        },
      },
    },
  };
};

const expanderColumn = (expanderColumnWidth = '40px') => {
  return {
    id: 'expander',
    header: () => null,
    cell: ({ row }) => (
      <div
        className="cursor-pointer d-flex justify-content-center"
        onClick={row.getToggleExpandedHandler()}
        style={{ cursor: 'pointer' }}
      >
        {row.getIsExpanded() ? (
          <FontAwesomeIcon icon="chevron-down" />
        ) : (
          <FontAwesomeIcon icon="chevron-right" />
        )}
      </div>
    ),
    meta: {
      headerProps: {
        className: 'text-center',
        style: { width: expanderColumnWidth },
      },
      cellProps: {
        className: 'text-center',
      },
    },
  };
};

const useAdvanceTable = ({
  columns,
  data,
  initialState,
  // Sort
  sortable,
  sortingState = [],
  onSortingChange,
  // Pagination
  totalItems,
  enablePagination = true,
  paginationState,
  onPaginationChange,
  // Selection/ Expander
  selection,
  selectionColumnWidth,
  selectionHeaderClassname,
  expandable = false,
  expandableColumnWidth,
  tableConfig = {},
}) => {
  let tableColumns = columns;
  const [expanded, setExpanded] = useState({});

  if (selection) {
    tableColumns = [
      selectionColumn(selectionColumnWidth, selectionHeaderClassname),
      ...tableColumns,
    ];
  }

  if (expandable) {
    tableColumns = [expanderColumn(expandableColumnWidth), ...tableColumns];
  }

  const { autoExpandAll } = tableConfig || {};

  useEffect(() => {
    if (autoExpandAll && data.length > 0) {
      const newExpandedState = {};
      data.forEach((_, index) => {
        newExpandedState[index] = true;
      });
      setExpanded(newExpandedState);
    }
  }, [data, autoExpandAll]);

  const state = {
    ...initialState,
    sorting: sortingState,
    pagination: enablePagination ? paginationState : { pageSize: data.length },
    expanded,
  };

  // Server-side configurations
  const paginationConfig = enablePagination
    ? {
        manualPagination: true,
        rowCount: totalItems,
        onPaginationChange,
      }
    : {};

  const sortingConfig = sortable
    ? {
        enableSorting: true,
        manualSorting: true,
        sortDescFirst: false,
        onSortingChange,
      }
    : {
        enableSorting: false,
      };

  const table = useReactTable({
    data,
    columns: tableColumns,
    state,
    ...sortingConfig,
    ...paginationConfig,
    enableExpanding: expandable,
    getRowCanExpand: () => expandable,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    ...(enablePagination && { getPaginationRowModel: getPaginationRowModel() }),
    getExpandedRowModel: getExpandedRowModel(),
    onExpandedChange: setExpanded,
  });

  return table;
};

export default useAdvanceTable;
